import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { Lock } from 'grommet-icons';
import { COUNTRIES } from '@RedflagAI/node-utility/utils/constants.utils';
import { useAppTheme } from '@Components/Context';
import { ProductFormSingleTextInput, ProductFormDropdownSelect } from '@Components/Partial/Product/NewSearch';

const BasicDetailsStep = ({
  small,
  basicDetails,
  setBasicDetails,
  lockedFields = {},
  emailError = null,
}) => {
  const { theme: { page } } = useAppTheme();

  const hasLockedFields = Object.values(lockedFields).some(Boolean);

  const readOnly = (value) => (
    <Box
      style={{
        background: 'var(--page-input-bg)',
        border: 'solid 1px var(--page-input-border)',
        padding: '0.65rem 1rem',
        borderRadius: 'var(--app-radius)',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Text size="0.8rem" color={page.primaryText}>{value || '—'}</Text>
      <Box direction="row" align="center" gap="0.25rem">
        <Lock size="0.8rem" color={page.secondaryText} />
        <Text size="0.7rem" color={page.secondaryText}>Pre-filled</Text>
      </Box>
    </Box>
  );

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>Account Details</Text>
        <Text size="0.85rem" color={page.secondaryText}>
          We&apos;ll use this information to set up your account and send updates.
        </Text>
      </Box>
      <Box
        round="var(--app-radius)"
        pad="1rem"
        background={page.cardBg}
        gap="0.5rem"
      >
        <Box wrap direction={small ? 'column' : 'row'}>
          <Box width={small ? '100%' : '48%'} flex={false} gap="0.5rem" margin={{ bottom: '0.75rem' }}>
            <Text size="1rem" color={page.primaryText} weight={600}>Company or Channel name *</Text>
            <Box width={small ? '100%' : '80%'}>
              {lockedFields.companyName ? readOnly(basicDetails.companyName) : (
                <ProductFormSingleTextInput
                  key="companyName"
                  focusHighlight={page.focusHighlight}
                  placeholder="Enter the name of your channel or company (e.g. Redflag AI)"
                  small={small}
                  value={basicDetails.companyName || ''}
                  handleFormValues={(val) => setBasicDetails({ ...basicDetails, companyName: val })}
                />
              )}
            </Box>
          </Box>
          <Box width={small ? '100%' : '48%'} flex={false} gap="0.5rem" margin={{ bottom: '0.75rem' }}>
            <Text size="1rem" color={page.primaryText} weight={600}>Full Name *</Text>
            <Box width={small ? '100%' : '80%'}>
              {lockedFields.fullName ? readOnly(basicDetails.fullName) : (
                <ProductFormSingleTextInput
                  key="fullName"
                  name="fullName"
                  autoComplete="name"
                  focusHighlight={page.focusHighlight}
                  placeholder="Enter your full name (e.g. John Doe)"
                  small={small}
                  value={basicDetails.fullName || ''}
                  handleFormValues={(val) => setBasicDetails({ ...basicDetails, fullName: val })}
                />
              )}
            </Box>
          </Box>
          <Box width={small ? '100%' : '48%'} flex={false} gap="0.5rem" margin={{ bottom: '0.75rem' }}>
            <Text size="1rem" color={page.primaryText} weight={600}>Email *</Text>
            <Box width={small ? '100%' : '80%'}>
              {lockedFields.email ? readOnly(basicDetails.email) : (
                <ProductFormSingleTextInput
                  key="email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  focusHighlight={page.focusHighlight}
                  placeholder="Enter a valid email (e.g. you@company.com)"
                  small={small}
                  value={basicDetails.email || ''}
                  handleFormValues={(val) => setBasicDetails({ ...basicDetails, email: val })}
                />
              )}
              {emailError && (
                <Text size="0.8rem" color="status-error" margin={{ top: '0.25rem' }}>
                  {emailError}
                </Text>
              )}
            </Box>
          </Box>
          <Box width={small ? '100%' : '48%'} flex={false} gap="0.5rem" margin={{ bottom: '0.75rem' }}>
            <Text size="1rem" color={page.primaryText} weight={600}>Country</Text>
            <Box width={small ? '100%' : '80%'}>
              {lockedFields.country ? readOnly(basicDetails.country) : (
                <ProductFormDropdownSelect
                  searchable
                  keepLabel
                  selectable
                  simpleOptions={COUNTRIES}
                  handleFormValues={(val) => setBasicDetails({ ...basicDetails, country: val })}
                  value={basicDetails.country || ''}
                  label={basicDetails.country || 'Select country...'}
                  placeholder="Select country..."
                  focusHighlight={page.focusHighlight}
                />
              )}
            </Box>
          </Box>
          <Box width={small ? '100%' : '48%'} flex={false} gap="0.5rem" margin={{ bottom: '0.75rem' }}>
            <Text size="1rem" color={page.primaryText} weight={600}>Company Phone</Text>
            <Box width={small ? '100%' : '80%'}>
              {lockedFields.phone ? readOnly(basicDetails.phone) : (
                <ProductFormSingleTextInput
                  key="phone"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  focusHighlight={page.focusHighlight}
                  placeholder="Include country code (e.g. +1 555 000 0000)"
                  value={basicDetails.phone}
                  handleFormValues={(val) => setBasicDetails({ ...basicDetails, phone: val })}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {hasLockedFields && (
        <Text size="0.8rem" color={page.secondaryText}>
          If any of the pre-filled information above is incorrect, please reach out to&nbsp;
          <a href="mailto:contact@redflagai.co" style={{ color: page.altHighlight }}>
            contact@redflagai.co
          </a>
          .
        </Text>
      )}
    </Box>
  );
};

BasicDetailsStep.propTypes = {
  small: PropTypes.bool.isRequired,
  basicDetails: PropTypes.shape({
    companyName: PropTypes.string,
    fullName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
  setBasicDetails: PropTypes.func.isRequired,
  lockedFields: PropTypes.shape({
    companyName: PropTypes.bool,
    fullName: PropTypes.bool,
    email: PropTypes.bool,
    phone: PropTypes.bool,
    country: PropTypes.bool,
  }),
  emailError: PropTypes.string,
};

export default BasicDetailsStep;
